import { I18n } from "@assets/strings";
import { useObservable } from "micro-observables";
import React from "react";
import { useHistory } from "@core/routing/useHistory";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { TertiaryButton } from "@ui/components/buttons/tertiaryButton";
import { CategoryEditor } from "@ui/components/category/categoryEditor";
import { PrimaryBold } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { CourseStatusLabel } from "@ui/components/course/courseStatusLabel";
import { ColorSelector } from "@ui/components/inputs/colorSelector";
import { RichTextInput } from "@ui/components/inputs/draft/richTextInput";
import { stack } from "@ui/components/layout";
import { LevelEditor } from "@ui/components/levelEditor";
import { ContentShareroomEditor } from "@ui/components/lms/contentShareroomEditor";
import { PublisherSelector } from "@ui/components/lms/publisherSelector";
import { useConfirmation } from "@ui/modals/useConfirmation";
import { quizColors } from "@ui/styles/colors";
import { theme } from "@ui/styles/theme";
import { gap, mobile } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";
import { useQuizEditorController } from "@ui/pages/lms/quiz/quizEditorPage/quizEditorController/quizEditorControllerContext";

interface QuizDetailsEditorProps extends React.HTMLAttributes<HTMLDivElement> {
	quizId?: string;
	loading: boolean;
	onSaveQuiz: (track?: "track", toast?: "toast", redirect?: "root" | "editor") => void;
}
export const QuizDetailsEditor: React.FC<QuizDetailsEditorProps> = ({ quizId, loading, onSaveQuiz, ...htmlProps }) => {
	const { quizService } = useServices();
	const controller = useQuizEditorController();
	const quiz = useObservable(controller.quiz);

	const askDeleteConfirmation = useConfirmation(
		I18n.t("quiz.delete.title"),
		I18n.t("delete"),
		I18n.t("quiz.delete.message")
	);
	const history = useHistory();

	return (
		<Container {...htmlProps}>
			<StyledQuizStatusLabel status={quiz.status} />
			<DetailsBlock>
				<PrimaryBold>{I18n.t("quiz.editor.details.settings")}</PrimaryBold>
				<CategoryEditor
					category={quiz.category}
					subCategory={quiz.subCategory}
					setCategory={(c) => controller.setCategory(c)}
					setSubCategory={(s) => controller.setSubCategory(s)}
					label={I18n.t("category.placeholder.quiz")}
				/>
				<LevelEditor
					value={quiz.level}
					onChange={(value) => controller.setLevel(value)}
					placeholder={I18n.t("quiz.editor.level.placeholder")}
					optional
					isQuizz
				/>
			</DetailsBlock>
			<PresentationBlock>
				<PrimaryBold>{I18n.t("quiz.editor.details.presentation")}</PrimaryBold>
				<DescriptionEditor
					value={quiz.description}
					onChange={(value) => controller.setDescription(value)}
					placeholder={I18n.t("quiz.editor.description.placeholder") + "*"}
				/>
				<ColorSelector
					color={quiz.color ?? theme.colors.vividBlue}
					onColorChange={(c) => controller.setColor(c)}
					colors={quizColors}
				/>
			</PresentationBlock>
			<ContentShareroomEditor
				draft={quiz}
				onToggleWorkspace={(id) => controller.toggleWorkspace(id)}
				onTogglePrivacy={(toggled) => controller.setVisibility(toggled ? "PRIVATE" : "PUBLIC")}
			/>
			<PublicationBlock>
				<PrimaryBold>{I18n.t("quiz.editor.details.publication")}</PrimaryBold>
				<LoadingButton
					buttonType="secondary"
					loading={loading}
					disabled={quiz.status === "PUBLISHED" ? !controller.isValidForPublication() : !controller.isValidForDraft()}
					onClick={() => onSaveQuiz("track", "toast", "editor")}
				>
					{I18n.t(quiz.status === "PUBLISHED" ? "quiz.editor.save.published" : "quiz.save.draft")}
				</LoadingButton>
				<PublisherSelector
					initialContent={quiz}
					onPublisherChange={(publisher) => {
						controller.setPublisher(publisher);
					}}
					onKeepOwnershipChange={(keepOwnership) => {
						controller.setOwnership(keepOwnership);
					}}
				/>
				<LoadingButton
					loading={loading}
					disabled={!controller.isValidForPublication()}
					onClick={() => {
						if (quiz.status === "PUBLISHED") {
							onSaveQuiz("track", "toast", "root");
						} else {
							controller.setStatus("PUBLISHED");
						}
					}}
				>
					{I18n.t("publish")}
				</LoadingButton>
			</PublicationBlock>
			<TertiaryActions>
				{quiz.status === "PUBLISHED" && (
					<TertiaryButton onClick={() => controller.setStatus("ARCHIVED")}>
						{I18n.t("quiz.editor.archive")}
					</TertiaryButton>
				)}
				{!!quizId && (
					<TertiaryButton
						onClick={() =>
							askDeleteConfirmation(async () => {
								await quizService.deleteQuiz(quizId);
								history.push(Path.Root);
								createToast(I18n.t("quiz.toast.delete.title"), I18n.t("quiz.toast.delete.message"), "success");
							})
						}
					>
						{I18n.t("quiz.editor.delete")}
					</TertiaryButton>
				)}
			</TertiaryActions>
		</Container>
	);
};

const Container = styled.div`
	${stack(20, "flex-start", "stretch")};
`;

const PublicationBlock = styled(RoundedBlock)`
	padding: 20px;
	${gap(20)};
`;

const DetailsBlock = styled(RoundedBlock)`
	padding: 20px;
	${gap(20)};
`;

const PresentationBlock = styled(RoundedBlock)`
	padding: 20px;
	${gap(20)};
`;

const DescriptionEditor = styled(RichTextInput)`
	${({ theme }) => theme.containerStyles.roundedBlockStyle};
	min-height: 100px;
	margin-top: 20px;
	cursor: text;

	.public-DraftEditor-content,
	.public-DraftEditorPlaceholder-root {
		padding: 20px;
	}
	.public-DraftEditorPlaceholder-root {
		opacity: 0.8;
	}
`;

const TertiaryActions = styled.div`
	align-self: center;
	@media ${mobile} {
		margin-top: 20px;
	}
	${gap(10)};

	> button {
		margin: 0 auto;
		text-decoration: none;
		${({ theme }) => theme.textStyles.label};
		font-size: 0.9375rem;
	}
`;

const StyledQuizStatusLabel = styled(CourseStatusLabel)`
	padding: 15px 10px;
	font-size: 0.9375rem;
	border-radius: 13px;
`;
